import Point from "@arcgis/core/geometry/Point";

/**
 * Converts an ArrayBuffer or a number array representing bytes into a Base64 encoded data URL.
 * Assumes the image type is PNG.
 *
 * @param buffer - The ArrayBuffer or number[] containing the image byte data.
 * @returns A Base64 encoded data URL string (e.g., "data:image/png;base64,..."), or an empty string if conversion fails or input is invalid.
 */
export const convertBytesToBase64Url = (buffer: ArrayBuffer | number[] | null | undefined): string => {
    if (!buffer || (Array.isArray(buffer) && buffer.length === 0) || (buffer instanceof ArrayBuffer && buffer.byteLength === 0)) {
      return '';
    }
  
    let bytes: Uint8Array;
    if (Array.isArray(buffer)) {
      // If it's a number array, create Uint8Array from it
      bytes = new Uint8Array(buffer);
    } else if (buffer instanceof ArrayBuffer) {
      // If it's already an ArrayBuffer, create a Uint8Array view
      bytes = new Uint8Array(buffer);
    } else {
      // Invalid input type
      console.error("Invalid input type for convertBytesToBase64Url. Expected ArrayBuffer or number[].");
      return '';
    }
  
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
  
    try {
      // Assuming PNG, adjust if the type can vary and be determined elsewhere
      return `data:image/png;base64,${window.btoa(binary)}`;
    } catch (e) {
      console.error("Error converting byte data to Base64:", e);
      return ''; // Return empty string or a placeholder image URL on error
    }
  };

export const getDimensionsForImage = (extent, mapView) => {
  const __minScreenshotSize = 500;

  // Create a point object for the lower left and upper right corner of the extent
  const llPoint = new Point({
    x: extent.xmin,
    y: extent.ymin,
    spatialReference: mapView.spatialReference,
  });

  const urPoint = new Point({
    x: extent.xmax,
    y: extent.ymax,
    spatialReference: mapView.spatialReference,
  });

  // Convert the lower left and upper right points to screen coordinates
  const llScreenPoint = mapView.toScreen(llPoint);
  const urScreenPoint = mapView.toScreen(urPoint);
  let imageWidth = urScreenPoint.x - llScreenPoint.x;
  let imageHeight = llScreenPoint.y - urScreenPoint.y;
  const minDimension = ((imageWidth < imageHeight) ? imageWidth : imageHeight);

  if (minDimension < __minScreenshotSize) {
    const ratio = __minScreenshotSize / minDimension;
    imageWidth = imageWidth * ratio;
    imageHeight = imageHeight * ratio;
  }

  const area = {
    x: llScreenPoint.x,
    y: urScreenPoint.y,
    width: urScreenPoint.x - llScreenPoint.x,
    height: llScreenPoint.y - urScreenPoint.y,
  };

  return { imageWidth, imageHeight, area };
}